"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HelpCircle, ChevronDown } from "lucide-react";
import { useAppSelector } from "@/store/hooks";
import { HOME_FAQS } from "@/data/home-faqs";

const FAQSection = () => {
  const { mode } = useAppSelector((state) => state.theme);
  const [activeIndex, setActiveIndex] = useState<number | null>(0);

  return (
    <section
      className={`py-24 relative overflow-hidden transition-colors duration-700 ${mode === "dark" ? "bg-[#0B0F19]" : "bg-slate-50"}`}
    >
      {/* Background Ambience */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`absolute top-0 -left-64 w-[500px] h-[500px] rounded-full blur-[100px] transition-all duration-700 ${mode === "dark" ? "bg-[#8B31FF]/20 opacity-100" : "bg-[#8B31FF]/10 opacity-50"}`}
        />
        <div
          className={`absolute bottom-0 -right-64 w-[500px] h-[500px] rounded-full blur-[100px] transition-all duration-700 ${mode === "dark" ? "bg-[#2B00A4]/20 opacity-100" : "bg-[#2B00A4]/10 opacity-50"}`}
        />
        {/* Abstract Grid Pattern Overlay */}
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${mode === "dark" ? "opacity-[0.03]" : "opacity-[0.05]"}`}
          style={{
            backgroundImage:
              mode === "dark"
                ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
                : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className={`inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full border backdrop-blur-md transition-all duration-300 ${
              mode === "dark"
                ? "border-[#8B31FF]/30 bg-[#8B31FF]/10"
                : "border-purple-100 bg-purple-50"
            }`}
          >
            <HelpCircle
              className={`w-4 h-4 ${mode === "dark" ? "text-[#8B31FF]" : "text-[#8B31FF]"}`}
            />
            <span
              className={`font-semibold text-xs uppercase tracking-wider transition-colors duration-300 ${mode === "dark" ? "text-[#8B31FF]" : "text-[#8B31FF]"}`}
            >
              Got Questions?
            </span>
          </motion.div>

          <h2
            className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 tracking-tight leading-[1.15] ${mode === "dark" ? "text-white" : "text-gray-900"}`}
          >
            Frequently Asked{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B31FF] to-[#8B31FF]">
              Questions
            </span>
          </h2>

          <p
            className={`text-base sm:text-lg max-w-2xl mx-auto leading-relaxed transition-colors duration-300 ${mode === "dark" ? "text-gray-400" : "text-gray-600"}`}
          >
            Everything you need to know about working with Hubmicroo — from
            pricing and timelines to support and technology.
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {HOME_FAQS.map((faq, index) => {
            const isActive = activeIndex === index;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: Math.min(index, 6) * 0.05 }}
              >
                <button
                  onClick={() => setActiveIndex(isActive ? null : index)}
                  aria-expanded={isActive}
                  className={`w-full cursor-pointer text-left p-5 md:p-6 rounded-lg transition-all duration-300 border ${
                    isActive
                      ? mode === "dark"
                        ? "bg-black/40 border-[#8B31FF]/40 shadow-[0_8px_40px_rgba(79,17,194,0.15)]"
                        : "bg-white border-[#8B31FF]/30 shadow-[0_8px_40px_rgba(79,17,194,0.1)]"
                      : mode === "dark"
                        ? "bg-white/5 border-white/10 hover:border-[#8B31FF]/30 hover:bg-white/[0.07]"
                        : "bg-white/70 border-gray-200 hover:border-[#8B31FF]/30 hover:shadow-md"
                  }`}
                >
                  <div className="flex justify-between items-center gap-4">
                    {/* Index number + question */}
                    <div className="flex items-center gap-4">
                      <span
                        className={`flex-none w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black transition-all duration-300 ${
                          isActive
                            ? "bg-gradient-to-br from-[#8B31FF] to-[#8B31FF] text-white shadow-md"
                            : mode === "dark"
                              ? "bg-white/10 text-gray-400"
                              : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <h3
                        className={`text-base md:text-lg font-bold tracking-tight transition-all duration-300 ${
                          isActive
                            ? "bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] bg-clip-text text-transparent"
                            : mode === "dark"
                              ? "text-white"
                              : "text-gray-900"
                        }`}
                      >
                        {faq.question}
                      </h3>
                    </div>

                    {/* Chevron */}
                    <div
                      className={`flex-none w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300 ${
                        isActive
                          ? "bg-gradient-to-br from-[#8B31FF] to-[#8B31FF] text-white rotate-180 shadow-md"
                          : mode === "dark"
                            ? "bg-white/5 text-gray-400 border border-white/10"
                            : "bg-white text-gray-400 border border-gray-200"
                      }`}
                    >
                      <ChevronDown size={18} />
                    </div>
                  </div>

                  <AnimatePresence initial={false}>
                    {isActive && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                          duration: 0.35,
                          ease: [0.04, 0.62, 0.23, 0.98],
                        }}
                        className="overflow-hidden"
                      >
                        <p
                          className={`pt-4 mt-4 border-t leading-relaxed text-sm md:text-base transition-colors duration-300 ${
                            mode === "dark"
                              ? "border-white/10 text-gray-400"
                              : "border-gray-200 text-gray-600"
                          }`}
                        >
                          {faq.answer}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
